import React, { useEffect, useState } from 'react';
import { useMood } from '../../contexts/MoodContext';

/**
 * ReflectionPanel — optional free-text answers to weekly/monthly prompts.
 *
 * These are the user's own words, kept entirely separate from the automatic
 * summaries, and filed per period (so last week's reflection stays with last
 * week). Each can be saved, edited (save again) or deleted (clear + save), and
 * nothing is stored when history is off.
 */
const WEEKLY_PROMPTS = [
  'What was one positive moment this week?',
  'What felt challenging?',
  'What helped you feel better?',
  'What would you like to focus on next week?',
];
const MONTHLY_PROMPTS = [
  'What moments stood out this month?',
  'What activities helped you feel good?',
  'What would you like to change next month?',
  'What are you grateful for?',
];

const ReflectionField: React.FC<{ period: 'week' | 'month'; periodKey: string; prompt: string }> = ({
  period,
  periodKey,
  prompt,
}) => {
  const { reflections, saveReflection } = useMood();
  const stored = reflections.find((r) => r.period === period && r.periodKey === periodKey && r.prompt === prompt);
  const [text, setText] = useState(stored?.text ?? '');
  const [savedTick, setSavedTick] = useState(false);

  // Re-sync when the period (week/month navigation) changes under us.
  useEffect(() => {
    setText(stored?.text ?? '');
    setSavedTick(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodKey, prompt]);

  const dirty = text.trim() !== (stored?.text ?? '');

  const save = () => {
    saveReflection(period, periodKey, prompt, text);
    setSavedTick(true);
  };

  return (
    <div className="mm-reflect__field">
      <label className="mm-reflect__prompt">{prompt}</label>
      <textarea
        className="mm-journal__input"
        rows={2}
        maxLength={1000}
        value={text}
        placeholder="Optional — a line or two, just for you."
        onChange={(e) => { setText(e.target.value); setSavedTick(false); }}
      />
      <div className="mm-reflect__actions">
        {stored && !dirty && !savedTick && (
          <button type="button" className="mm-link-btn" onClick={() => { setText(''); saveReflection(period, periodKey, prompt, ''); }}>
            Delete
          </button>
        )}
        {savedTick && !dirty && <span className="mm-saved-note">Saved ✓</span>}
        <button type="button" className="mm-btn mm-btn--ghost mm-btn--sm" onClick={save} disabled={!dirty}>
          {stored ? 'Update' : 'Save'}
        </button>
      </div>
    </div>
  );
};

export const ReflectionPanel: React.FC<{ period: 'week' | 'month'; periodKey: string }> = ({ period, periodKey }) => {
  const prompts = period === 'week' ? WEEKLY_PROMPTS : MONTHLY_PROMPTS;
  return (
    <div className="mm-reflect">
      {prompts.map((p) => (
        <ReflectionField key={p} period={period} periodKey={periodKey} prompt={p} />
      ))}
    </div>
  );
};

export default ReflectionPanel;
